import type { HTMLAttributes } from "react";

import { cn } from "@/components/ui/lib/utils";

export type CircularProgressProps = HTMLAttributes<SVGSVGElement> & {
	/** Progress value from 0 to 100 */
	value?: number;
	/** Size of the circular progress in pixels */
	size?: number;
	/** Stroke width */
	strokeWidth?: number;
	/** Whether to show indeterminate animation */
	indeterminate?: boolean;
};

export function CircularProgress({
	value = 0,
	size = 16,
	strokeWidth = 2,
	indeterminate = false,
	className,
	...props
}: CircularProgressProps) {
	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const progress = Math.min(100, Math.max(0, value));
	const strokeDashoffset = circumference - (progress / 100) * circumference;

	return (
		<svg
			width={size}
			height={size}
			viewBox={`0 0 ${size} ${size}`}
			className={cn(indeterminate && "animate-spin", className)}
			{...props}
		>
			{/* Background circle */}
			<circle
				cx={size / 2}
				cy={size / 2}
				r={radius}
				fill="none"
				stroke="currentColor"
				strokeWidth={strokeWidth}
				className="opacity-20"
			/>
			{/* Progress circle */}
			<circle
				cx={size / 2}
				cy={size / 2}
				r={radius}
				fill="none"
				stroke="currentColor"
				strokeWidth={strokeWidth}
				strokeLinecap="round"
				strokeDasharray={circumference}
				strokeDashoffset={indeterminate ? circumference * 0.75 : strokeDashoffset}
				className="transition-all duration-300"
				style={{
					transformOrigin: "center",
					transform: "rotate(-90deg)",
				}}
			/>
		</svg>
	);
}
